import { FieldErrors, FieldErrorsImpl } from "react-hook-form";
import { TestValidationError } from "../../components/test-validation-error.type";
import { forIn } from "lodash";
import { HZ_VALUES } from "constants/audiogram";
import { isBlank } from "utils/common";
import {
  Ear,
  EarPureToneValues,
  PureToneReportValues,
} from "../pure-tone-audiogram.type";
import { PureToneAudiogramReport } from "services/types/reports.type";

export const getPureToneDefaultRowArray = () =>
  Array.from({ length: 13 }, () => null);

export const AC_UNMASKED_LEFT = "AC_UNMASKED_LEFT";
export const AC_MASKED_LEFT = "AC_MASKED_LEFT";
export const BC_UNMASKED_LEFT = "BC_UNMASKED_LEFT";
export const BC_MASKED_LEFT = "BC_MASKED_LEFT";
export const MCL_LEFT = "MCL_LEFT";
export const UCL_LEFT = "UCL_LEFT";
export const AC_UNMASKED_RIGHT = "AC_UNMASKED_RIGHT";
export const AC_MASKED_RIGHT = "AC_MASKED_RIGHT";
export const BC_UNMASKED_RIGHT = "BC_UNMASKED_RIGHT";
export const BC_MASKED_RIGHT = "BC_MASKED_RIGHT";
export const MCL_RIGHT = "MCL_RIGHT";
export const UCL_RIGHT = "UCL_RIGHT";
export const SOUNDFIELD_AIDED_LEFT = "SOUNDFIELD_AIDED_LEFT";
export const SOUNDFIELD_UNAIDED_LEFT = "SOUNDFIELD_UNAIDED_LEFT";
export const SOUNDFIELD_AIDED_RIGHT = "SOUNDFIELD_AIDED_RIGHT";
export const SOUNDFIELD_UNAIDED_RIGHT = "SOUNDFIELD_UNAIDED_RIGHT";
export const AC_UNMASKED_LEFT_NR = "AC_UNMASKED_LEFT_NR";
export const AC_MASKED_LEFT_NR = "AC_MASKED_LEFT_NR";
export const BC_UNMASKED_LEFT_NR = "BC_UNMASKED_LEFT_NR";
export const BC_MASKED_LEFT_NR = "BC_MASKED_LEFT_NR";
export const AC_UNMASKED_RIGHT_NR = "AC_UNMASKED_RIGHT_NR";
export const AC_MASKED_RIGHT_NR = "AC_MASKED_RIGHT_NR";
export const BC_UNMASKED_RIGHT_NR = "BC_UNMASKED_RIGHT_NR";
export const BC_MASKED_RIGHT_NR = "BC_MASKED_RIGHT_NR";
export const UCL_LEFT_NR = "UCL_LEFT_NR";
export const UCL_RIGHT_NR = "UCL_RIGHT_NR";
export const SOUNDFIELD_AIDED_LEFT_NR = "SOUNDFIELD_AIDED_LEFT_NR";
export const SOUNDFIELD_UNAIDED_LEFT_NR = "SOUNDFIELD_UNAIDED_LEFT_NR";
export const SOUNDFIELD_AIDED_RIGHT_NR = "SOUNDFIELD_AIDED_RIGHT_NR";
export const SOUNDFIELD_UNAIDED_RIGHT_NR = "SOUNDFIELD_UNAIDED_RIGHT_NR";
export const formatIsSelectedOnlyPureToneTestInput = ({
  left,
  right,
  isSelected,
}: PureToneReportValues) => {
  const pureToneTestInput: {
    pureToneAudiogramReport: Partial<PureToneAudiogramReport>;
  } = {
    pureToneAudiogramReport: {
      isSelected,
      isAirConductionLSelected: left.airConduction.isSelected,
      isAirConductionMaskingLevelLSelected:
        left.airConductionMaskingLevel.isSelected,
      isBoneConductionLSelected: left.boneConduction.isSelected,
      isBoneConductionMaskingLevelLSelected:
        left.boneConductionMaskingLevel.isSelected,
      isMclLSelected: left.mcl.isSelected,
      isUclLSelected: left.ucl.isSelected,
      isSoundFieldAidedLSelected: left.soundfieldAided.isSelected,
      isSoundFieldUnaidedLSelected: left.soundfieldUnaided.isSelected,

      isAirConductionRSelected: right.airConduction.isSelected,
      isAirConductionMaskingLevelRSelected:
        right.airConductionMaskingLevel.isSelected,
      isBoneConductionRSelected: right.boneConduction.isSelected,
      isBoneConductionMaskingLevelRSelected:
        right.boneConductionMaskingLevel.isSelected,
      isMclRSelected: right.mcl.isSelected,
      isUclRSelected: right.ucl.isSelected,
      isSoundFieldAidedRSelected: right.soundfieldAided.isSelected,
      isSoundFieldUnaidedRSelected: right.soundfieldUnaided.isSelected,
    },
  };
  return pureToneTestInput;
};

export const formatPureToneValue = (value: any) => {
  if (isBlank(value)) return null;

  if (typeof value === "object" && !isBlank(value?.value)) return value.value;

  return String(value).trim();
};

export const formatPureToneTestInput = (testInput: PureToneReportValues) => {
  const { left, right, isSelected } = testInput;

  const pureToneTestInput: {
    pureToneAudiogramReport: PureToneAudiogramReport;
  } = {
    pureToneAudiogramReport: {
      //left
      isSelected,
      airConductionL: left.airConduction.numerics.map(formatPureToneValue),
      airConductionLAnnotation:
        left.airConduction.values.map(formatPureToneValue),
      isAirConductionLSelected: left.airConduction.isSelected,

      airConductionMaskingLevelL:
        left.airConductionMaskingLevel.numerics.map(formatPureToneValue),
      airConductionMaskingLevelLAnnotation:
        left.airConductionMaskingLevel.values.map(formatPureToneValue),
      isAirConductionMaskingLevelLSelected:
        left.airConductionMaskingLevel.isSelected,

      boneConductionL: left.boneConduction.numerics.map(formatPureToneValue),
      boneConductionLAnnotation:
        left.boneConduction.values.map(formatPureToneValue),
      isBoneConductionLSelected: left.boneConduction.isSelected,

      boneConductionMaskingLevelL:
        left.boneConductionMaskingLevel.numerics.map(formatPureToneValue),
      boneConductionMaskingLevelLAnnotation:
        left.boneConductionMaskingLevel.values.map(formatPureToneValue),
      isBoneConductionMaskingLevelLSelected:
        left.boneConductionMaskingLevel.isSelected,

      mclL: left.mcl.numerics.map(formatPureToneValue),
      mclLAnnotation: left.mcl.values.map(formatPureToneValue),
      isMclLSelected: left.mcl.isSelected,

      uclL: left.ucl.numerics.map(formatPureToneValue),
      uclLAnnotation: left.ucl.values.map(formatPureToneValue),
      isUclLSelected: left.ucl.isSelected,

      soundfieldAidedL: left.soundfieldAided.numerics.map(formatPureToneValue),
      soundfieldAidedLAnnotation:
        left.soundfieldAided.values.map(formatPureToneValue),
      isSoundFieldAidedLSelected: left.soundfieldAided.isSelected,

      soundfieldUnaidedL:
        left.soundfieldUnaided.numerics.map(formatPureToneValue),
      soundfieldUnaidedLAnnotation:
        left.soundfieldUnaided.values.map(formatPureToneValue),
      isSoundFieldUnaidedLSelected: left.soundfieldUnaided.isSelected,

      //right
      airConductionR: right.airConduction.numerics.map(formatPureToneValue),
      airConductionRAnnotation:
        right.airConduction.values.map(formatPureToneValue),
      isAirConductionRSelected: right.airConduction.isSelected,

      airConductionMaskingLevelR:
        right.airConductionMaskingLevel.numerics.map(formatPureToneValue),
      airConductionMaskingLevelRAnnotation:
        right.airConductionMaskingLevel.values.map(formatPureToneValue),
      isAirConductionMaskingLevelRSelected:
        right.airConductionMaskingLevel.isSelected,

      boneConductionR: right.boneConduction.numerics.map(formatPureToneValue),
      boneConductionRAnnotation:
        right.boneConduction.values.map(formatPureToneValue),
      isBoneConductionRSelected: right.boneConduction.isSelected,

      boneConductionMaskingLevelR:
        right.boneConductionMaskingLevel.numerics.map(formatPureToneValue),
      boneConductionMaskingLevelRAnnotation:
        right.boneConductionMaskingLevel.values.map(formatPureToneValue),
      isBoneConductionMaskingLevelRSelected:
        right.boneConductionMaskingLevel.isSelected,

      mclR: right.mcl.numerics.map(formatPureToneValue),
      mclRAnnotation: right.mcl.values.map(formatPureToneValue),
      isMclRSelected: right.mcl.isSelected,

      uclR: right.ucl.numerics.map(formatPureToneValue),
      uclRAnnotation: right.ucl.values.map(formatPureToneValue),
      isUclRSelected: right.ucl.isSelected,

      soundfieldAidedR: right.soundfieldAided.numerics.map(formatPureToneValue),
      soundfieldAidedRAnnotation:
        right.soundfieldAided.values.map(formatPureToneValue),
      isSoundFieldAidedRSelected: right.soundfieldAided.isSelected,

      soundfieldUnaidedR:
        right.soundfieldUnaided.numerics.map(formatPureToneValue),
      soundfieldUnaidedRAnnotation:
        right.soundfieldUnaided.values.map(formatPureToneValue),
      isSoundFieldUnaidedRSelected: right.soundfieldUnaided.isSelected,
    },
  };

  return pureToneTestInput;
};

export const getAirConductionPointStyle = (
  ear: Ear,
  value: number | string | null,
  isNR: boolean = false
) => {
  if (isNR) {
    if (ear === "left") {
      return value === null || value === ""
        ? AC_UNMASKED_LEFT_NR
        : AC_MASKED_LEFT_NR;
    } else {
      return value === null || value === ""
        ? AC_UNMASKED_RIGHT_NR
        : AC_MASKED_RIGHT_NR;
    }
  }
  if (ear === "left") {
    return value === null || value === "" ? AC_UNMASKED_LEFT : AC_MASKED_LEFT;
  } else {
    return value === null || value === "" ? AC_UNMASKED_RIGHT : AC_MASKED_RIGHT;
  }
};

export const getBoneConductionPointStyle = (
  ear: Ear,
  value: number | string | null,
  isNR: boolean = false
) => {
  if (isNR) {
    if (ear === "left") {
      return value === null || value === ""
        ? BC_UNMASKED_LEFT_NR
        : BC_MASKED_LEFT_NR;
    } else {
      return value === null || value === ""
        ? BC_UNMASKED_RIGHT_NR
        : BC_MASKED_RIGHT_NR;
    }
  }
  if (ear === "left") {
    return value === null || value === "" ? BC_UNMASKED_LEFT : BC_MASKED_LEFT;
  } else {
    return value === null || value === "" ? BC_UNMASKED_RIGHT : BC_MASKED_RIGHT;
  }
};

export const getMCLPointStyle = (ear: Ear) => {
  return ear === "left" ? MCL_LEFT : MCL_RIGHT;
};

export const getUCLPointStyle = (ear: Ear, isNR: boolean = false) => {
  if (isNR) {
    return ear === "left" ? UCL_LEFT_NR : UCL_RIGHT_NR;
  }
  return ear === "left" ? UCL_LEFT : UCL_RIGHT;
};

export const getSoundfieldAidedPointStyle = (
  ear: Ear,
  isNR: boolean = false
) => {
  if (isNR) {
    return ear === "left"
      ? SOUNDFIELD_AIDED_LEFT_NR
      : SOUNDFIELD_AIDED_RIGHT_NR;
  }
  return ear === "left" ? SOUNDFIELD_AIDED_LEFT : SOUNDFIELD_AIDED_RIGHT;
};

export const getSoundfieldUnaidedPointStyle = (
  ear: Ear,
  isNR: boolean = false
) => {
  if (isNR) {
    return ear === "left"
      ? SOUNDFIELD_UNAIDED_LEFT_NR
      : SOUNDFIELD_UNAIDED_RIGHT_NR;
  }
  return ear === "left" ? SOUNDFIELD_UNAIDED_LEFT : SOUNDFIELD_UNAIDED_RIGHT;
};

const fieldLabels: Record<string, string> = {
  airConduction: "AC",
  airConductionMaskingLevel: "ACML",
  boneConduction: "BC",
  boneConductionMaskingLevel: "BCML",
  mcl: "MCL",
  ucl: "UCL",
  soundfieldAaided: "A",
  soundfieldUnaided: "S",
};

const getEarErrors = (
  ear: string,
  errors: FieldErrorsImpl<EarPureToneValues>
) => {
  const result: TestValidationError[] = [];

  forIn(errors, (fieldGroupValue, fieldGroupName) => {
    forIn(fieldGroupValue?.values, (value: any, key) => {
      if (value) {
        result.push({
          label: `${ear} ${fieldLabels[fieldGroupName]} ${
            HZ_VALUES[Number(key)]
          }`,
          message: value?.message,
        });
      }
    });
  });

  return result;
};

export const getErrors = (
  errors: FieldErrors<PureToneReportValues>
): TestValidationError[] => {
  let result: TestValidationError[] = [];
  if (errors.left) {
    result = [...result, ...getEarErrors("Left", errors.left)];
  }

  if (errors.right) {
    result = [...result, ...getEarErrors("Right", errors.right)];
  }

  return result;
};
